/**
 * Утилиты для работы с полями в RieltorPropertyDetails
 * Проверка пустых значений, форматирование, валидация
 */

// Типы для проверки значений
type FieldValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<any>
  | Record<string, any>;

/**
 * Проверяет, является ли значение пустым или недействительным
 */
export const isEmpty = (value: FieldValue): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (typeof value === "number") return isNaN(value);
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
};

/**
 * Проверяет, имеет ли значение какое-то содержимое
 */
export const hasValue = (value: FieldValue): boolean => !isEmpty(value);

/**
 * Проверяет, является ли значение валидной датой
 */
export const isValidDate = (value: any): boolean => {
  if (!value) return false;
  const date = new Date(value);
  return !isNaN(date.getTime());
};

/**
 * Проверяет, является ли значение валидным URL
 */
export const isValidUrl = (value: string): boolean => {
  if (!value || typeof value !== "string") return false;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

/**
 * Проверяет, является ли значение валидным телефоном
 */
export const isValidPhone = (value: string): boolean => {
  if (!value || typeof value !== "string") return false;
  const phoneRegex = /^\+?[\d\s\-\(\)]{7,}$/;
  return phoneRegex.test(value.replace(/\s/g, ""));
};

/**
 * Безопасно получает вложенное значение из объекта
 */
export const safeGet = <T = any>(
  obj: any,
  path: string,
  fallback?: T,
): T | undefined => {
  const keys = path.split(".");
  let result = obj;

  for (const key of keys) {
    if (result == null || typeof result !== "object") {
      return fallback;
    }
    result = result[key];
  }

  return result !== undefined ? result : fallback;
};

/**
 * Форматирует дату в локальном формате
 */
export const formatDate = (
  date: Date | string,
  locale: string = "ru-RU",
): string => {
  if (!isValidDate(date)) return "";

  const d = new Date(date);
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

/**
 * Маскирует телефонный номер
 */
export const maskPhone = (phone: string): string => {
  if (!phone || !isValidPhone(phone)) return "";

  // Убираем все нецифровые символы кроме +
  const cleaned = phone.replace(/[^\d+]/g, "");

  // Различные форматы маскирования в зависимости от длины
  if (cleaned.length >= 10) {
    return cleaned.replace(
      /(\+?\d{1,3})(\d{2,3})(\d{3})(\d{2})(\d{2})/,
      "$1$2***$4**",
    );
  }

  return phone.replace(/\d/g, "*");
};

/**
 * Обрезает URL для отображения
 */
export const truncateUrl = (url: string, maxLength: number = 25): string => {
  if (!url || !isValidUrl(url)) return "";

  if (url.length <= maxLength) return url;

  // Удаляем протокол для экономии места
  const withoutProtocol = url.replace(/^https?:\/\//, "");

  if (withoutProtocol.length <= maxLength) return withoutProtocol;

  return withoutProtocol.substring(0, maxLength - 3) + "...";
};

/**
 * Получает статус из значения с переводом
 */
export const getStatusDisplay = (
  status: string,
  t: (key: string) => string,
): string => {
  if (!status) return t("status.unknown");

  // Normalize status to handle different formats
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, "_");
  const statusKey = `status.${normalizedStatus}`;
  const translated = t(statusKey);

  // If translation not found, try status key without prefix
  if (translated === statusKey) {
    // Try common status mappings
    const statusMappings: Record<string, string> = {
      ready: "status.ready",
      in_progress: "status.in_progress",
      not_started: "status.not_started",
      under_construction: "status.under_construction",
      exclusive: "status.exclusive",
      planned: "status.planned",
      unknown: "status.unknown",
    };

    const mappedKey = statusMappings[normalizedStatus];
    if (mappedKey) {
      const mappedTranslated = t(mappedKey);
      return mappedTranslated !== mappedKey ? mappedTranslated : status;
    }

    return status; // Return original if no translation found
  }

  return translated;
};

/**
 * Проверяет, нужно ли показывать секцию (есть ли в ней хотя бы одно заполненное поле)
 */
export const shouldShowSection = (
  fields: Record<string, FieldValue>,
): boolean => {
  return Object.values(fields).some(hasValue);
};

/**
 * Фильтрует объект, оставляя только поля с значениями
 */
export const filterEmptyFields = <T extends Record<string, any>>(
  obj: T,
): Partial<T> => {
  const result: Partial<T> = {};

  Object.entries(obj).forEach(([key, value]) => {
    if (hasValue(value)) {
      result[key as keyof T] = value;
    }
  });

  return result;
};

/**
 * Создает fallback значение для отображения
 */
export const getFallbackValue = (
  value: FieldValue,
  t: (key: string) => string,
): string => {
  if (hasValue(value)) {
    if (typeof value === "string") return value;
    if (typeof value === "number") return value.toString();
    if (typeof value === "boolean") {
      return value ? t("common.yes") : t("common.no");
    }
    return String(value);
  }

  return t("common.notSpecified");
};
